import { useState, useEffect } from 'react'
import { X, Search, UserPlus, User, Building2 } from 'lucide-react'
import { Cliente } from '../../types'
import clientesService from '../../services/clientes'
import ClienteModal from '../clientes/ClienteModal'
import toast from 'react-hot-toast'

interface ClienteSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectCliente: (cliente: Cliente) => void
}

export default function ClienteSelectionModal({
  isOpen,
  onClose,
  onSelectCliente
}: ClienteSelectionModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [filteredClientes, setFilteredClientes] = useState<Cliente[]>([])
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null)
  const [loading, setLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadClientes()
    }
  }, [isOpen])

  useEffect(() => {
    if (searchTerm) {
      const filtered = clientes.filter(cliente => {
        const searchLower = searchTerm.toLowerCase()
        return (
          cliente.nome.toLowerCase().includes(searchLower) ||
          cliente.email?.toLowerCase().includes(searchLower) ||
          cliente.telefonePrincipal?.includes(searchTerm) ||
          cliente.nif?.includes(searchTerm)
        )
      })
      setFilteredClientes(filtered)
    } else {
      setFilteredClientes(clientes)
    }
  }, [searchTerm, clientes])

  const loadClientes = async () => {
    setLoading(true)
    try {
      const response = await clientesService.getClientes({ page: 1, limit: 1000 })
      setClientes(response.data)
      setFilteredClientes(response.data)
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
      toast.error('Erro ao carregar clientes')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectCliente = (cliente: Cliente) => {
    setSelectedCliente(cliente)
  }

  const handleContinue = () => {
    if (selectedCliente) {
      onSelectCliente(selectedCliente)
      resetModal()
    } else {
      toast.error('Por favor, selecione um cliente')
    }
  }

  const handleClienteCreated = (cliente: Cliente) => {
    setShowCreateModal(false)
    onSelectCliente(cliente)
    resetModal()
  }

  const resetModal = () => {
    setSearchTerm('')
    setSelectedCliente(null)
    setFilteredClientes(clientes)
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
          <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold">Selecionar Cliente</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 flex-1 overflow-auto">
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium mb-4">O cliente já existe?</h3>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Pesquisar cliente por nome, email, telefone ou NIF..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <>
                  {searchTerm && filteredClientes.length > 0 && (
                    <div className="border rounded-lg max-h-60 overflow-y-auto">
                      {filteredClientes.slice(0, 10).map((cliente) => (
                        <div
                          key={cliente.id}
                          onClick={() => handleSelectCliente(cliente)}
                          className={`p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 ${
                            selectedCliente?.id === cliente.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <div className="mt-1">
                                {cliente.tipoCliente === 'EMPRESA' ? (
                                  <Building2 className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <User className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium">{cliente.nome}</p>
                                {cliente.email && (
                                  <p className="text-sm text-gray-600">{cliente.email}</p>
                                )}
                                {cliente.telefonePrincipal && (
                                  <p className="text-sm text-gray-600">{cliente.telefonePrincipal}</p>
                                )}
                                {cliente.nif && (
                                  <p className="text-sm text-gray-500">NIF: {cliente.nif}</p>
                                )}
                              </div>
                            </div>
                            {selectedCliente?.id === cliente.id && (
                              <div className="text-blue-600">
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {searchTerm && filteredClientes.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      Nenhum cliente encontrado
                    </div>
                  )}
                </>
              )}

              <div className="pt-4 border-t">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <UserPlus className="h-5 w-5" />
                  <span>Criar Novo Cliente</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 border-t flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedCliente}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCliente
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continuar
            </button>
          </div>
        </div>
      </div>

      <ClienteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleClienteCreated}
      />
    </>
  )
}